import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import useApi from "@/hooks/useApi";
import useSubscriptions from "@/hooks/useSubscriptions";
import ShopifyWelcomeTour from "./ShopifyWelcomeTour";
import useTourState from "./useTourState";

/**
 * Only real for a NEW Shopify install. Reuses the 30-day onboarding window
 * ReviewRequestTriggerService already established (server/Velaro.Admin/Services/
 * ReviewRequestTriggerService.cs).
 */
export const RECENT_INSTALL_WINDOW_DAYS = 30;

export function isRecentInstall(createdAt: unknown): boolean {
  if (typeof createdAt !== "string") return false;
  const ms = Date.parse(createdAt);
  if (Number.isNaN(ms)) return false;
  const ageDays = (Date.now() - ms) / (1000 * 60 * 60 * 24);
  return ageDays >= 0 && ageDays <= RECENT_INSTALL_WINDOW_DAYS;
}

type TourApi = ReturnType<typeof useTourState>;

interface TourContextValue {
  /** True only for a genuinely new Shopify install the person is entitled to see. */
  eligible: boolean;
  /** Non-null only when eligible. */
  tour: TourApi | null;
  open: () => void;
}

const NOT_ELIGIBLE: TourContextValue = { eligible: false, tour: null, open: () => {} };

const ShopifyTourContext = createContext<TourContextValue | null>(null);

/** Null when rendered outside the provider (the launcher then renders nothing). */
export function useShopifyTour(): TourContextValue | null {
  return useContext(ShopifyTourContext);
}

/**
 * Hosts the Moshky Shopify tour ABOVE the routed outlet (mounted by MainLayout), so it survives
 * navigation and can auto-open on the first admin page a new Shopify merchant lands on.
 *
 * Gating — all must hold before anything renders:
 *  1. subscription.enableShopify === true (the entitlement; GET Shopify/Integrations is itself
 *     [Subscription(Feature.Shopify)] and 403s without it — Shopify-provisioned trial sites
 *     lacking the flag are fixed server-side, not by widening this gate), and
 *  2. GET Shopify/Integrations/ returns 200 with a config row created within
 *     RECENT_INSTALL_WINDOW_DAYS, and
 *  3. the person is authenticated (tour state is per-user, server-side).
 * Failing to confirm a Shopify connection fails closed; failing to read tour state fails open.
 *
 * Per-user tour state (useTourState) is only fetched by TourHost, which mounts once conditions
 * 1-2 hold, so non-Shopify sites make no OnboardingTour request on every page load.
 */
export function ShopifyTourProvider({ children }: { children: React.ReactNode }) {
  const api = useApi();
  const { subscription } = useSubscriptions();
  const [hasShopify, setHasShopify] = useState<boolean | null>(null);

  const entitled = subscription?.enableShopify === true;

  useEffect(() => {
    if (!entitled) {
      setHasShopify(false);
      return;
    }
    let cancelled = false;
    api.messaging
      .get("Shopify/Integrations/")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : [];
        setHasShopify(rows.some((row) => isRecentInstall(row?.createdAt)));
      })
      .catch(() => {
        // Can't confirm a Shopify connection -> no Shopify-specific tour (fail closed).
        if (!cancelled) setHasShopify(false);
      });
    return () => {
      cancelled = true;
    };
  }, [api, entitled]);

  if (hasShopify !== true) {
    return (
      <ShopifyTourContext.Provider value={NOT_ELIGIBLE}>{children}</ShopifyTourContext.Provider>
    );
  }
  return <TourHost>{children}</TourHost>;
}

function TourHost({ children }: { children: React.ReactNode }) {
  const tour = useTourState();
  const { state, loading: stateLoading, recordProgress } = tour;
  const [open, setOpen] = useState(false);
  const [autoOpened, setAutoOpened] = useState(false);

  const neverEngaged =
    !!state && !state.startedAt && !state.dismissedAt && !state.completedAt;

  useEffect(() => {
    if (autoOpened || stateLoading) return;
    if (neverEngaged) {
      setOpen(true);
      setAutoOpened(true);
      // Stamp StartedAt the moment the tour auto-opens so closing the tab without clicking
      // anything doesn't make it auto-open again on every later visit.
      recordProgress(0);
    }
  }, [stateLoading, neverEngaged, autoOpened, recordProgress]);

  const value = useMemo<TourContextValue>(
    () => ({ eligible: true, tour, open: () => setOpen(true) }),
    [tour]
  );

  return (
    <ShopifyTourContext.Provider value={value}>
      {children}
      {open && <ShopifyWelcomeTour tour={tour} onClose={() => setOpen(false)} />}
    </ShopifyTourContext.Provider>
  );
}
